import { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, Shield, Store, ChefHat, Truck, ArrowLeft } from "lucide-react";
import API_URL from "@/utils/api";
import Logo from "@/components/ui/Logo";

const API = API_URL;

export default function StaffLogin() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Auto-redirect if already logged in
  useEffect(() => {
    if (user && user.role !== "customer") {
      console.log(`[StaffLogin] User ${user.email} already logged in, redirecting...`);
      switch (user.role) {
        case "admin":
        case "support":
          navigate("/admin", { replace: true });
          break;
        case "cashier":
          navigate("/admin/pos", { replace: true }); // Better to go to POS directly
          break;
        case "kitchen":
          navigate("/admin/kitchen", { replace: true });
          break;
        case "driver":
          navigate("/driver", { replace: true });
          break;
        case "screen":
          navigate("/pedidos-local", { replace: true });
          break;
        default:
          navigate("/admin", { replace: true });
      }
    }
  }, [user, navigate]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/login`,
        formData,
        {
          withCredentials: true,
          timeout: 10000 // 10 segundos de timeout
        }
      );

      const user = response.data.user;

      // Verify this is a staff member, not a customer
      if (user.role === "customer") {
        toast.error("Este acceso es solo para personal del restaurante");
        setIsLoading(false);
        return;
      }

      // Store token
      if (response.data.access_token) {
        localStorage.setItem("session_token", response.data.access_token);
      }

      setUser(user);

      // Show welcome message based on role
      const roleNames = {
        admin: "Administrador",
        support: "Soporte Técnico",
        cashier: "Cajero",
        kitchen: "Cocina",
        driver: "Repartidor",
        screen: "Pantalla de Pedidos"
      };
      toast.success(`¡Bienvenido ${roleNames[user.role] || user.name}!`);

      // Redirect based on role with tenant awareness
      setTimeout(() => {
        // If we are on the Hub domain, we must ensure we use the correct tenant prefix
        // otherwise HostContext will default to 'admin' as the tenant ID.
        const isHub = window.location.hostname.includes("pedido.");
        const tenantPrefix = (isHub && user.company_id) ? `/${user.company_id}` : "/admin";

        switch (user.role) {
          case "admin":
          case "support":
            navigate(tenantPrefix, { replace: true });
            break;
          case "cashier":
            navigate(`${tenantPrefix}/pos`, { replace: true }); 
            break;
          case "kitchen":
            navigate(`${tenantPrefix}/kitchen`, { replace: true });
            break;
          case "driver":
            navigate("/driver", { replace: true });
            break;
          case "screen":
            navigate(`${tenantPrefix}/pedidos-local`, { replace: true });
            break;
          default:
            navigate(tenantPrefix, { replace: true });
        }
      }, 100);

    } catch (error) {
      const message = error.response?.data?.detail || "Credenciales incorrectas";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-black z-0 pointer-events-none" />

      {/* Back button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate("/")}
        className="absolute top-4 left-4 text-white hover:bg-gray-800 z-10"
        data-testid="staff-back-btn"
      >
        <ArrowLeft className="w-5 h-5" />
      </Button>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-24 h-24 rounded-2xl border-2 border-primary/30 flex items-center justify-center mb-4 overflow-hidden bg-black/40 backdrop-blur-md shadow-lg shadow-primary/10" style={{ borderColor: 'var(--primary, #EAB308)' }}>
            <Logo size={40} variant="icon" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-widest uppercase">
             <Logo variant="full" size={28} className="justify-center" />
          </h1>
          <p className="text-gray-400 text-[10px] uppercase font-bold tracking-[0.2em] mt-2">Acceso Personal</p>
        </div>

        {/* Login Card */}
        <Card className="bg-gray-900/80 border-gray-800 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-center text-lg">Iniciar Sesión</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Email</label>
                <Input
                  type="email"
                  placeholder="tu.email@restaurante.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                  data-testid="staff-email-input"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Contraseña</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white pr-10"
                    required
                    data-testid="staff-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-primary hover:opacity-90 text-black font-semibold"
                style={{ backgroundColor: 'var(--primary)' }}
                disabled={isLoading}
                data-testid="staff-login-btn"
              >
                {isLoading ? "Ingresando..." : "Ingresar"}
              </Button>
            </form>

            {/* Role icons */}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-xs text-gray-500 text-center mb-3">Acceso para:</p>
              <div className="flex justify-center gap-6">
                <div className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-xs text-gray-500">Admin</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Store className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-xs text-gray-500">Cajero</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <ChefHat className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="text-xs text-gray-500">Cocina</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-xs text-gray-500">Repartidor</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help text */}
        <p className="text-center text-gray-500 text-xs mt-4">
          ¿No tienes cuenta? Contacta al administrador
        </p>
      </div>
    </div>
  );
}
